import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  FileText,
  ArrowLeft,
  Calendar,
  Edit3,
  Trash2,
  Download,
  Loader2,
  CheckCircle,
  Clock,
  Info
} from 'lucide-react';
import clientReportsAPI from '../api/clientReports';
import { reportsAPI } from '../api/reports';

const ClientReportsListPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch reports for this campaign
  const { data: reports, isLoading } = useQuery({
    queryKey: ['client-reports', campaignId],
    queryFn: () => clientReportsAPI.getCampaignReports(campaignId).then(r => r.data),
    enabled: !!campaignId
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (reportId) => clientReportsAPI.deleteReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries(['client-reports', campaignId]);
    }
  });

  const handleCreateNew = () => {
    navigate(`/client-report/${campaignId}/new`);
  };

  const handleEdit = (reportId) => {
    navigate(`/client-report/${campaignId}/${reportId}`);
  };

  const handleDelete = async (reportId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this report?')) {
      deleteMutation.mutate(reportId);
    }
  };

  const handleExportPDF = async (reportId, e) => {
    e.stopPropagation();
    try {
      const { data } = await clientReportsAPI.getFullReportData(reportId);

      // Use server-side PDF generation
      const response = await reportsAPI.generatePDF(data);

      // Create download link from blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename — "<report title> - <game>" so multiple reports for
      // the same campaign get distinct, readable names. Falls back to campaign
      // name + date when the report has no title.
      const clean = (s) => (s || '').replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
      const rawGame = data.campaign?.game;
      const gameName = clean(typeof rawGame === 'object' ? (rawGame?.title || rawGame?.name) : rawGame);
      const titlePart = clean(data.report?.title);
      if (titlePart) {
        link.download = `${gameName ? `${titlePart} - ${gameName}` : titlePart}.pdf`;
      } else {
        const campaignName = clean(data.campaign?.name) || 'report';
        const dateStr = new Date().toISOString().split('T')[0];
        link.download = `${campaignName} - ${dateStr}.pdf`;
      }

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateRange = (start, end) => {
    if (!start && !end) return 'No date range set';
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sc-pink" />
        <span className="ml-2 text-gray-600">Loading reports...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/campaigns/${campaignId}`)}
                className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Client Reports</h1>
                <p className="text-sm text-gray-500">Create and manage reports for clients</p>
              </div>
            </div>
            
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-4 py-2 bg-sc-pink text-white rounded-lg text-sm font-medium hover:bg-sc-pink/90 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Report
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {reports?.length === 0 ? (
          <div className="card-pink-accent bg-white rounded-xl border border-gray-200 p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No reports yet</h3>
            <p className="text-gray-500 mb-6">
              Create your first client report to share campaign performance with stakeholders.
            </p>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-4 py-2 bg-sc-pink text-white rounded-lg text-sm font-medium hover:bg-sc-pink/90 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Report
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <div
                key={report.id}
                onClick={() => handleEdit(report.id)}
                className="card-pink-accent bg-white rounded-xl border border-gray-200 p-6 hover:border-sc-pink hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg ${
                      report.status === 'published' 
                        ? 'bg-green-100' 
                        : 'bg-amber-100'
                    }`}>
                      <FileText className={`w-6 h-6 ${
                        report.status === 'published' 
                          ? 'text-green-600' 
                          : 'text-amber-600'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {report.title || `Report: ${formatDateRange(report.reportPeriodStart, report.reportPeriodEnd)}`}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          report.status === 'published'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {report.status === 'published' ? (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Published
                            </>
                          ) : (
                            <>
                              <Clock className="w-3 h-3 mr-1" />
                              Draft
                            </>
                          )}
                        </span>
                      </div>
                      {report.title && (report.reportPeriodStart || report.reportPeriodEnd) && (
                        <p className="text-sm text-gray-600 mt-1">
                          {formatDateRange(report.reportPeriodStart, report.reportPeriodEnd)}
                        </p>
                      )}
                      <div className="flex items-center mt-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        Created {formatDate(report.createdAt)}
                        {report.updatedAt !== report.createdAt && (
                          <span className="ml-2">
                            • Updated {formatDate(report.updatedAt)}
                          </span>
                        )}
                      </div>
                      {/* Preview of content */}
                      <div className="mt-3 space-y-1">
                        {report.activitiesCompleted && (
                          <p className="text-sm text-gray-600 truncate max-w-xl">
                            <span className="font-medium">Activities:</span> {report.activitiesCompleted}
                          </p>
                        )}
                        {report.overallAnalysis && (
                          <p className="text-sm text-gray-600 truncate max-w-xl">
                            <span className="font-medium">Analysis:</span> {report.overallAnalysis}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <div className="relative group">
                      <button
                        onClick={(e) => handleExportPDF(report.id, e)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-sc-pink/10 text-sc-pink transition-colors text-sm font-medium"
                      >
                        <Download className="w-4 h-4" />
                        Download Report
                        <Info className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                      <div className="absolute bottom-full right-0 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-10">
                        <p className="font-medium mb-1">Download Campaign Report</p>
                        <p className="text-gray-300">YouTube data provided by the YouTube API Services. Data valid for 30 days from fetch date.</p>
                        <div className="absolute bottom-0 right-6 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(report.id);
                      }}
                      className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
                      title="Edit"
                    >
                      <Edit3 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => handleDelete(report.id, e)}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientReportsListPage;
